import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Header } from '@/components/Header';
import { ProductGrid } from '@/components/ProductGrid';
import { Cart } from '@/components/Cart';
import { AuthModal } from '@/components/AuthModal';
import { CheckoutModal } from '@/components/CheckoutModal';
import { Toaster } from '@/components/ui/toaster';
import { useToast } from '@/hooks/use-toast';

interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  image_url: string;
  material: string;
  size: string;
  stock_quantity: number;
}

interface CartItem {
  id: string;
  product_id: string;
  quantity: number;
  product: Product;
}

const Index = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [user, setUser] = useState(null);
  const [showCart, setShowCart] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setUser(session?.user ?? null);
        if (session?.user) {
          loadCartItems();
        } else {
          setCartItems([]);
        }
      }
    );

    loadProducts();

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (user) {
      loadCartItems();
    }
  }, [user]);

  const loadProducts = async () => {
    const { data, error } = await supabase
      .from('products')
      .select('*')
      .eq('is_active', true)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error loading products:', error);
      toast({
        title: "加载失败",
        description: "无法加载商品信息",
        variant: "destructive",
      });
    } else {
      setProducts(data || []);
    }
  };

  const loadCartItems = async () => {
    if (!user) return;

    const { data, error } = await supabase
      .from('cart_items')
      .select(`
        *,
        product:products(*)
      `)
      .eq('user_id', user.id);

    if (error) {
      console.error('Error loading cart:', error);
    } else {
      setCartItems(data || []);
    }
  };

  const addToCart = async (productId: string) => {
    if (!user) {
      setShowAuth(true);
      return;
    }

    const { error } = await supabase
      .from('cart_items')
      .upsert({
        user_id: user.id,
        product_id: productId,
        quantity: 1
      }, {
        onConflict: 'user_id,product_id'
      });

    if (error) {
      console.error('Error adding to cart:', error);
      toast({
        title: "添加失败",
        description: "无法添加商品到购物车",
        variant: "destructive",
      });
    } else {
      toast({
        title: "添加成功",
        description: "商品已添加到购物车",
      });
      loadCartItems();
    }
  };

  const updateCartQuantity = async (itemId: string, quantity: number) => {
    if (quantity <= 0) {
      const { error } = await supabase
        .from('cart_items')
        .delete()
        .eq('id', itemId);

      if (error) {
        console.error('Error removing item:', error);
      } else {
        loadCartItems();
      }
    } else {
      const { error } = await supabase
        .from('cart_items')
        .update({ quantity })
        .eq('id', itemId);

      if (error) {
        console.error('Error updating quantity:', error);
      } else {
        loadCartItems();
      }
    }
  };

  const getTotalAmount = () => {
    return cartItems.reduce((total, item) => total + (item.product.price * item.quantity), 0);
  };

  const handleCheckout = () => {
    if (cartItems.length === 0) {
      toast({
        title: "购物车为空",
        description: "请先添加商品到购物车",
        variant: "destructive",
      });
      return;
    }
    setShowCheckout(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
          <p className="mt-4 text-muted-foreground">加载中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header 
        user={user}
        cartItemsCount={cartItems.length}
        onCartClick={() => setShowCart(true)}
        onAuthClick={() => setShowAuth(true)}
      />
      
      <main className="container mx-auto px-4 py-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
            手工饰品精品店
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            精心挑选的手工饰品，每一件都承载着匠人的心血与创意，为您带来独特的美丽体验
          </p>
        </div>

        <ProductGrid 
          products={products}
          onAddToCart={addToCart}
        />
      </main>

      <Cart
        isOpen={showCart}
        onClose={() => setShowCart(false)}
        items={cartItems}
        onUpdateQuantity={updateCartQuantity}
        onCheckout={handleCheckout}
        totalAmount={getTotalAmount()}
      />

      <AuthModal
        isOpen={showAuth}
        onClose={() => setShowAuth(false)}
      />

      <CheckoutModal
        isOpen={showCheckout}
        onClose={() => setShowCheckout(false)}
        items={cartItems}
        totalAmount={getTotalAmount()}
        onSuccess={() => {
          setShowCheckout(false);
          setShowCart(false);
          loadCartItems();
          toast({
            title: "支付成功",
            description: "您的订单已完成，感谢您的购买！",
          });
        }}
      />

      <Toaster />
    </div>
  );
};

export default Index;